/**
 * Magic placeholder URL pattern used by template/seed figures
 * (e.g. "/api/placeholder/600/350"). There is no real endpoint —
 * 513+ blocks across the prod DB carry this pattern as a stand-in
 * for a missing image. Resolve to an inline SVG so the browser
 * never tries to GET a non-existent route.
 *
 * Treat this as a known data-URL convention, not a network fetch.
 */
const PLACEHOLDER_RE = /^\/api\/placeholder\/(\d+)\/(\d+)$/;

export function isPlaceholderSrc(src: string | null | undefined): boolean {
  return typeof src === "string" && PLACEHOLDER_RE.test(src);
}

/**
 * An uploaded image whose URL names a host.
 *
 * The API bakes its own public URL into the block at upload time — in
 * development that is `http://localhost:5001/uploads/...`. That works only for
 * a browser running on the same machine as the API. Open the same document
 * from a laptop against the server's IP and `localhost` means the laptop, so
 * the image silently fails to load while the file itself is perfectly fine
 * (observed 2026-09-09 on a document whose JPEG served 200 from the API and
 * showed nothing in the editor).
 *
 * Rewriting to a host-relative path makes the browser ask whichever origin is
 * serving the editor, which the dev server proxies to the API. It repairs
 * every document already carrying an absolute URL, without touching stored
 * data.
 */
const ABSOLUTE_UPLOAD_RE = /^https?:\/\/[^/]+(\/uploads\/.+)$/i;

export function resolveImageSrc(src: string | null | undefined): string {
  if (!src) return "";

  const absoluteUpload = src.match(ABSOLUTE_UPLOAD_RE);
  if (absoluteUpload) return absoluteUpload[1];

  const m = src.match(PLACEHOLDER_RE);
  if (!m) return src;
  const w = clamp(parseInt(m[1], 10), 20, 2000);
  const h = clamp(parseInt(m[2], 10), 20, 2000);
  const fontSize = Math.max(12, Math.min(w, h) / 10);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" preserveAspectRatio="xMidYMid meet"><rect width="100%" height="100%" fill="#f1f5f9"/><line x1="0" y1="0" x2="${w}" y2="${h}" stroke="#cbd5e1" stroke-width="1"/><line x1="${w}" y1="0" x2="0" y2="${h}" stroke="#cbd5e1" stroke-width="1"/><text x="50%" y="50%" text-anchor="middle" dy=".35em" font-family="system-ui,-apple-system,sans-serif" font-size="${fontSize}" fill="#94a3b8">${w}×${h}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function clamp(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}
